import { getCurrentInstance, onMounted, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue'

/**
 * Browser-persisted preferences — the ONE door to `localStorage`/`sessionStorage` in `app/`
 * (MAR-948, direction B, wave 1).
 *
 * WHY ONE DOOR. Web storage throws outright in a browser that blocks site data, in some private
 * windows, and on a full quota — and it does not exist at all during SSR. Before this, 24 files
 * touched storage directly (59 lines), each handling that its own way — and most not at all, so a
 * blocked storage could take a page, or the request path in `useApi`, down with it.
 * Every access here is client-only and wrapped, so a caller cannot forget either, and a failure
 * degrades to "no stored value" / "not remembered", which is what a preference is allowed to do.
 * `tests/unit/persistedPrefSweep.test.ts` fails if a bare `localStorage` appears anywhere else.
 *
 * Storage holds per-user conveniences only (collapsed panels, last sort, the chosen view). Anything
 * that must survive a device belongs on the server.
 *
 * "Client-only" is decided by whether the storage object is reachable, not by `import.meta.client`:
 * that is a build-time constant vitest never sets, so a test could not tell a write from a skipped
 * write (AlexAmin/marcus#93, which is where `stores/documents.ts` learned the same thing).
 */

export type StorageKind = 'local' | 'session'

/**
 * The browser's `Storage`, or `null` under SSR or when the browser refuses access. Reading the
 * property itself can throw (`SecurityError` with site data blocked), which is why this is a
 * function and not a constant. For the few callers that must hand a `Storage` to shared code
 * (`shared/web-chat/scope.ts`); everything else uses the functions below.
 */
export function browserStorage(kind: StorageKind = 'local'): Storage | null {
  try {
    const storage = kind === 'local' ? globalThis.localStorage : globalThis.sessionStorage
    return storage ?? null
  } catch {
    return null
  }
}

/** The stored string, or `null` when there is none — or no storage, or it threw. */
export function readPref(key: string, kind: StorageKind = 'local'): string | null {
  try {
    return browserStorage(kind)?.getItem(key) ?? null
  } catch {
    return null
  }
}

/** Store `value`, or remove the key when `value` is `null`. Best-effort: never throws. */
export function writePref(key: string, value: string | null, kind: StorageKind = 'local'): void {
  try {
    const storage = browserStorage(kind)
    if (!storage) return
    if (value === null) storage.removeItem(key)
    else storage.setItem(key, value)
  } catch {
    // Private mode or a full quota. Not remembering a preference is not a reason to fail the action.
  }
}

export interface PersistedPrefOptions<T> {
  /** How the value is written. Defaults to `String(value)`. */
  serialize?: (value: T) => string
}

/**
 * A ref that starts at `defaultValue`, is restored from storage, and writes itself back on change.
 *
 * `parse` both decodes and VALIDATES the stored string: return the value, or `undefined` to reject it
 * (a value from an older build, a hand-edited key, a corrupt JSON blob), and the default stays. A
 * throw inside `parse` counts as a rejection.
 *
 * Restore timing: inside a component that has not mounted yet it happens in `onMounted`, so the
 * first client render matches the server's markup (reading earlier is a hydration mismatch).
 * Anywhere else (a store, a composable called after mount) it happens immediately.
 *
 * `key` may be a getter (`() => \`marcus:batch-columns:${props.templateId}\``); when it changes, the
 * value is restored from the new key, or reset to the default when the new key holds nothing. A
 * `null` key means "nothing to persist under" — the ref still works, it is just not remembered.
 *
 * Writes are synchronous with the change, and a restore never writes back.
 */
export function usePersistedPref<T>(
  key: MaybeRefOrGetter<string | null>,
  defaultValue: T,
  parse: (raw: string) => T | undefined,
  options: PersistedPrefOptions<T> = {},
): Ref<T> {
  const serialize = options.serialize ?? ((value: T) => String(value))
  const pref = ref(defaultValue) as Ref<T>
  let restoring = false

  function restore() {
    const k = toValue(key)
    const raw = k ? readPref(k) : null
    let next: T | undefined
    if (raw !== null) {
      try { next = parse(raw) } catch { next = undefined }
    }
    restoring = true
    pref.value = next === undefined ? defaultValue : next
    restoring = false
  }

  watch(pref, (value) => {
    if (restoring) return
    const k = toValue(key)
    if (k) writePref(k, serialize(value))
  }, { flush: 'sync', deep: true })

  watch(() => toValue(key), () => restore())

  const instance = getCurrentInstance()
  if (instance && !instance.isMounted) onMounted(restore)
  else restore()

  return pref
}
